/* eslint max-len: 0 */
/* eslint react/no-unescaped-entities: 0 */

import React from "react";
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import styled, { keyframes } from "react-emotion";
import Img from "gatsby-image";
import Link from "gatsby-link";
import SEO from "../components/SEO";
import Container from "../components/Container";
import Content from "../components/Content";
import Suggestions from "../components/Suggestions";
import WaveNews from '../components/WaveNews';
import { Card } from "../components/Card";
import Button from "../components/Button";
import Footer from "../components/Footer";
import Line from "../components/Line";
import LinePad from "../components/LinePad";
import config from "../../config/website";

import SlideUp from "../components/ani/SlideUp";
import Browser from '../util/Browser';

import "../util/prism-okaida.css";

import ReactDisqusComments from "react-disqus-comments";
import DisqusContainer from "../components/DisqusContainer";

import Trophy from "../icons/fa-solid/Trophy";
import DraftingCompass from "../icons/fa-solid/DraftingCompass";

import CenterGrid from "../components/CenterGrid";
import SubNav from "../components/SubNav";

import FillBg from "../components/FillBg";
import FxVignette from "../components/FxVignette";
import FxTint from "../components/FxTint";

const IconSpan = styled.span`
  top: 0.17rem;
  left: -0.25rem;
  position: relative;
`;

const CompassIconSpan = styled.span`
  top: -0.75rem;
  position: relative;
`;

const FooterDiv = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.pure.bright};
  h1 {
    color: ${props => props.theme.colors.pure.bright};
    text-shadow: ${props => props.theme.set.light.shadow._3dThin};
  }
  @media (max-width: ${props => props.theme.breakpoints.s}) {
    top: -250px;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    animation-timing-function: ease-in;
    transform-origin: 16% 84%;
    translate(1.25remm, 0.9375 );
  }
  25% {
    animation-timing-function: ease-out;
    transform: scale(1.05);
    transform-origin: 16% 84%;
    translate(1.25remm, 0.9375 );
  }
  50% {
    transform: scale(1.125);
    animation-timing-function: ease-in;
    transform-origin: 16% 84%;
    translate(1.25remm, 0.9375 );
  }
  to {
    transform: scale(1);
    animation-timing-function: ease-out;
    transform-origin: 16% 84%;
    translate(1.25remm, 0.9375 );
  }
`;

const TintWrap = styled.div`
  background-color: ${props => props.theme.colors.night.medium};
  mix-blend-mode: overlay;
`;

const Wrapper = styled.div`
  height: 600px;
  position: relative;
  overflow: hidden;
  .gatsby-image-wrapper {
    z-index: 0;
    height: 600px;
    img {
      animation: ${pulse} 30s infinite;
      animation-delay: 1300ms;
    }
  }
  @media (max-width: ${props => props.theme.breakpoints.m}) {
    height: 500px;
    .gatsby-image-wrapper {
      height: 500px;
    }
  }
  @media (max-width: ${props => props.theme.breakpoints.s}) {
    height: 400px;
    .gatsby-image-wrapper {
      height: 400px;
    }
  }
`;

const Hero = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  color: ${props => props.theme.colors.pure.bright};
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${props => props.theme.layout.base};
  padding: 0 2rem;
  text-align: center;
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-Content: space-between;
  padding-left: 2rem;
  padding-right: 2rem;
  margin-bottom: 2rem;
  font-family: ${props => props.theme.fontFamily.button};
  text-shadow: 1px 0px 0 ${props => props.theme.colors.cool.dark},
    0px 1px 0 ${props => props.theme.colors.cool.dark},
    1px 1px 0 ${props => props.theme.colors.cool.dark};
  ${Card} {
    color: ${props => props.theme.set.light.p00};
    h2 {
      color: ${props => props.theme.set.light.h1};
      text-shadow: ${props => props.theme.set.light.shadow._3dThin};
    }
    margin-bottom: 2rem;
    text-align: center;
    flex-basis: calc(33.3% - 1rem);
    max-width: calc(33.3% - 1rem);
    width: calc(33.3% - 1rem);
    /*
    flex-basis: calc(99.9% * 1 / 3 - 1rem);
    max-width: calc(99.9% * 1 / 3 - 1rem);
    width: calc(99.9% * 1 / 3 - 1rem);
    */
   font-size: 1.25rem;
    h2 {
      position: relative;
      margin-bottom: 0.25rem;
    }
    @media (max-width: 1000px) {
      font-size: 1rem;
    }
    @media (max-width: 750px) {
      flex-basis: 100%;
      max-width: 100%;
      width: 100%;
      margin-bottom: 1.5rem;
    }
  }
`;

const TrophyDiv = styled.div`
  positon: absolute;
  display: inline-block;
  width: 2rem;
  height: 2rem;
  margin-right: 0.85rem;
  padding-bottom: 2.5rem;
  transform: translateY(0.1225px);
  svg {
    g {
      fill: ${props => props.theme.colors.pure.white};
      stroke-width: 24;
    }
  }
`;

const TitleH2 = styled.h2`
  font-size: 2.95rem;
  color: ${props => props.theme.colors.pure.bright};
  text-shadow: ${props => props.theme.set.light.shadow._3d};
`;

let SlideUp00 = styled.div``;

const Product = ({
  pathContext: { slug, left, right },
  data: { markdownRemark: postNode },
  location
}) => {
  const post = postNode.frontmatter;
  post.cover.childImageSharp.dutone = {
    highlight: "#465182",
    shadow: "#465182"
  };
  const { sizes } = post.cover.childImageSharp;
  if (!post.id) {
    post.id = slug;
  }

  Img.sizes = sizes;

  // ---------------------------------------------------------------
  // Disqus
  // ---------------------------------------------------------------
  const disqus = {
    shortname: "sondro",
    url: `${location.hostname}${post.title}`,
    identifier: location.pathname,
    title: post.title.text,
    language: "en"
  };
  // ---------------------------------------------------------------
  /*
 <div style={{ position: "relative", zIndex: "9", top: "8.5rem" }}>
          <SlideUp delay={51} fromOpacity={1} fromY='100px'>
            <WaveNews />
          </SlideUp>
        </div>
  */
  if (Browser() === 'chrome') {
    SlideUp00 = SlideUp;
  }
  return (
    <React.Fragment>
      <Helmet title={`${post.title} | ${config.siteTitle}`} />
      <SEO postPath={slug} postNode={postNode} postSEO />
      <Wrapper>
        <Hero>
          <SlideUp00 delay={25} fromY={"0px"} fromX={"32px"}>
            <TitleH2>{`${"\u00A0"}.${"\u00A0"}:${"\u00A0"}${
              post.title
              }${"\u00A0"}:${"\u00A0"}.`}</TitleH2>
          </SlideUp00>
        </Hero>
        <FxVignette />
        <FxTint />
        <WaveNews />
        <Img sizes={sizes} />
      </Wrapper>
      <Container>
        <FillBg>
          <SlideUp00 delay={250} fromY={"0px"}>
            <SubNav>
              <Link to="/design">
                <CompassIconSpan>
                  <DraftingCompass />
                </CompassIconSpan>
                <span>{`${"\u00A0"}:${"\u00A0"}DESIGN`}</span>
              </Link>
            </SubNav>
          </SlideUp00>
          <SlideUp00 delay={100} fromY={"12px"}>
            <CardWrapper>
              <Card>
                <h2>Brand / IP</h2>
                {post.brand}
              </Card>
              <Card>
                <h2>Tech</h2>
                {post.tech}
              </Card>
              <Card>
                <h2>Span</h2>
                {post.time}
              </Card>
            </CardWrapper>
            <Container>
              <Content input={postNode.html} />
              <DisqusContainer type="article">
                <Line />
                <ReactDisqusComments
                  shortname={disqus.shortname}
                  identifier={disqus.identifier}
                  // url={disqus.url}
                  title={disqus.title}
                  language={disqus.language}
                />
              </DisqusContainer>
            </Container>
            <SlideUp00 fromY={"0px"} fromX={"90px"} delay={1400}>
              <LinePad />
            </SlideUp00>
            <br />
            <CenterGrid>
              <SubNav>
                <Link to="/product">
                  <IconSpan>
                    <Trophy />
                  </IconSpan>
                  <span>{`${"\u00A0"}:${"\u00A0"}PRODUCT`}</span>
                </Link>
              </SubNav>
            </CenterGrid>
            <Container>
              <Suggestions left={left} right={right} />
              <div style={{ padding: "2.6823rem" }} />
            </Container>
          </SlideUp00>
        </FillBg>
      </Container>
      <Footer>
        <FooterDiv>
          <h1>Launch Your Dream!</h1>
          <Link to="/help">
            <Button type="main">
              <div style={{ height: "0.25rem" }} />
              <TrophyDiv>
                <Trophy />
              </TrophyDiv>
              {`:${"\u00A0"}:${"\u00A0"}New${"\u00A0"}Product${"\u00A0"}:${"\u00A0"}:`}
            </Button>
          </Link>
        </FooterDiv>
      </Footer>
    </React.Fragment>
  );
};

export default Product;

Product.propTypes = {
  pathContext: PropTypes.shape({
    slug: PropTypes.string.isRequired
  }),
  data: PropTypes.shape({
    markdownRemark: PropTypes.object.isRequired
  })
};

/* eslint no-undef: "off" */
export const pageQuery = graphql`
  query ProductPostBySlug($slug: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      frontmatter {
        title
        date(formatString: "MMM.DD.YYYY")
        brand
        tech
        time
        cover {
          childImageSharp {
            sizes(
              maxWidth: 1920
              duotone: { highlight: "#808080", shadow: "#303030", opacity: 93 }
              quality: 90
              traceSVG: { color: "#787878" }
            ) {
                ...GatsbyImageSharpSizes_withWebp_tracedSVG
              }
            resize(width: 1200) {
              src
              height
              width
            }
          }
        }
      }
      excerpt(pruneLength: 180)
      fields {
        slug
      }
    }
  }
`;
// duotone: { highlight: "#465182", shadow: "#20253c" }

//"#b69659"
//highlight: "#5ABDFF", shadow: "#3466DB"


// WEBPACK FOOTER //
// ./src/templates/product.jsx